import React, { useEffect, useState, useCallback } from "react";
import axios, { MEDIA_BASE_URL } from "../../axios/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "./Layout";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import PaginationControls from "../../Components/PaginationControls";
import RowsPerPageSelect from "../../Components/RowsPerPageSelect";
import ExportCsvButton from "../../Components/ExportCsvButton";
import DateRangeFilter from "../../Components/DateRangeFilter";

const AllServices = () => {
  const [services, setServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get("/services/getServices", {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });
      setServices(response.data.services);
      setTotalPages(response.data.totalPages);
      setTotalResults(response.data.totalResults);
    } catch (err) {
      toast.error("Failed to load services.");
    }
  }, [currentPage, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?"))
      return;

    try {
      await axios.delete(`/services/${id}`);
      toast.success("Service deleted successfully!");
      fetchServices();
    } catch (err) {
      toast.error("Failed to delete service.");
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const response = await axios.get("/services/getServices", {
      params: {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    });
    return response.data.services;
  }, [startDate, endDate]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const csvColumns = [
    { label: "Title", accessor: (row) => row.title },
    { label: "Price", accessor: (row) => row.price },
    { label: "Image", accessor: (row) => row.image },
  ];

  return (
    <>
      <Navbar />
      <Layout>
        <div className="container px-5 py-4">
          <ToastContainer />
          <h2 className="mb-4 fw-bold">All Services</h2>
          <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onClear={handleClearDates}
            />
            <div className="d-flex align-items-center gap-3">
              <ExportCsvButton
                filename="services.csv"
                columns={csvColumns}
                fetchRows={fetchAllForExport}
              />
              <RowsPerPageSelect
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No services found.
                    </td>
                  </tr>
                ) : (
                  services.map((service) => (
                    <tr key={service.id}>
                      <td>
                        <img
                          src={`${MEDIA_BASE_URL}/${service.image}`}
                          alt="service"
                          width={250}
                          height={150}
                          crossOrigin="anonymous"
                          className="rounded object-fit-cover"
                        />
                      </td>
                      <td>{service.title}</td>
                      <td>{service.price}</td>
                      <td>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="btn btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalResults}
            onPageChange={(page) =>
              setCurrentPage(Math.min(Math.max(page, 1), totalPages || 1))
            }
          />
        </div>
      </Layout>
      <Footer />
    </>
  );
};

export default AllServices;
